import React, { useEffect, useRef } from "react";

interface Props {
    onVisible: () => void;
    disabled?: boolean;
    /** Scroll container to observe within (for infinite scroll inside a scrollable div). */
    scrollRoot?: HTMLElement | null;
    /**
     * Selector for the closest ancestor to observe within, for a container
     * whose ref is not attached yet when this sentinel's effect runs.
     */
    scrollRootSelector?: string;
}

export const InfiniteScrollSentinel: React.FC<Props> = ({
    onVisible,
    disabled,
    scrollRoot,
    scrollRootSelector,
}) => {
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (disabled) {
            return;
        }

        const sentinel = sentinelRef.current;
        if (!sentinel) {
            return;
        }
        const root =
            scrollRoot ??
            (scrollRootSelector ? sentinel.closest(scrollRootSelector) : null);

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting) {
                    onVisible();
                }
            },
            { root, rootMargin: "100px", threshold: 0 },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [onVisible, disabled, scrollRoot, scrollRootSelector]);

    return <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />;
};
